import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import FavoriteButton from "@/components/base/FavoriteButton";
import ShareButton from "@/components/base/ShareButton";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { findNews, getRelatedNews } from "@/lib/data";

export default function NewsDetailPage() {
  const { newsId } = useParams();
  const news = findNews(newsId);

  useDocumentMeta({
    title: news ? `日漫 - ${news.title}` : "日漫 - 资讯详情",
    description: news ? news.summary : "日漫资讯详情页，阅读最新日本动画行业动态与深度解读。",
    keywords: news ? `${news.category},动漫资讯,日漫,${news.title}` : "动漫资讯,日漫",
  });

  if (!news) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-6 py-20">
          <EmptyState
            title="未找到这篇资讯"
            description="文章可能已被移除，返回资讯中心看看其他内容吧。"
          />
          <div className="mt-6 text-center">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回资讯中心
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const related = getRelatedNews(news, 3);

  return (
    <Layout>
      <article className="w-full px-4 md:px-6 pt-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "动漫资讯", to: "/news" },
              { label: news.category },
            ]}
          />

          <div className="mt-6 flex flex-wrap items-center gap-2 text-sm">
            <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 font-medium">{news.category}</span>
            <span className="text-foreground-500 flex items-center gap-1">
              <i className="ri-calendar-line" />
              {news.date}
            </span>
            <span className="text-foreground-500 flex items-center gap-1">
              <i className="ri-user-line" />
              {news.author}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-extrabold leading-snug text-foreground-950">
            {news.title}
          </h1>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">{news.summary}</p>
        </div>

        <div className="max-w-4xl mx-auto mt-8 rounded-lg overflow-hidden border border-background-200 bg-background-100">
          <img
            src={news.cover}
            alt={`${news.title} 配图`}
            title={`${news.title} 动漫资讯配图`}
            className="w-full aspect-[8/5] md:aspect-[16/8] object-top"
          />
        </div>

        <div className="max-w-3xl mx-auto mt-10">
          <div className="space-y-5">
            {news.content.map((paragraph, index) => (
              <p key={index} className="text-sm md:text-base leading-loose text-foreground-700">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-3">
              <FavoriteButton id={news.id} kind="news" />
              <ShareButton />
            </div>
            <Link
              to="/news"
              className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
            >
              <i className="ri-arrow-left-line" />
              返回资讯中心
            </Link>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">相关阅读</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {related.map((item) => (
              <NewsCard key={item.id} news={item} />
            ))}
          </div>
        </div>
      </article>
    </Layout>
  );
}